// Copyright (c) 2025 Greener Power Solutions.
// This file is part of Greener Power Solutions' software,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, IconButton, AppBar as MuiAppbar, Toolbar as MuiToolbar, Tooltip, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { GreenerLogo } from 'components/assets/GreenerLogo';
import { Icons } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import { ASSETS_VIEWS } from 'pages/Assets/AssetsViewToggle';
import { PROJECTS_VIEWS } from 'pages/Projects/Projects.utils';
import { canSeeAlerts, canSeeBlueprints } from 'utils/authorization';
import { Account } from './Account';
import { Alerts } from './Alerts';
import Blueprints from './Blueprints';
import Projects from './Projects';
import TopBarSearch from './TopBarSearch';
import UnitControl from './UnitControl';

const AppBar = styled(MuiAppbar)(({ theme }) => ({
  position: 'fixed',
  padding: theme.spacing(0.5, 0),
  boxShadow: 'none',
  backgroundColor: theme.palette.brand['forest-green'],
}));

const Toolbar = styled(MuiToolbar)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
}));

export function Topbar({
  toggleSidebar,
  openSearchDrawer,
  ...rest
}: {
  toggleSidebar: () => void;
  openSearchDrawer: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isLogoVisible = useMediaQuery('(min-width:650px)');

  const hideSideBarToggle =
    [
      ...Object.values(PROJECTS_VIEWS),
      ...Object.values(ASSETS_VIEWS),
      '/alerts',
      '/solutions',
      '/solutions/new',
      '/glossary',
      '/preferences',
      '/projects/new',
      '/test-data',
    ].includes(location.pathname) || location.pathname.startsWith('/solutions/');

  return (
    <AppBar {...rest}>
      <Toolbar variant="dense">
        <Box gap={0.5} display="flex" alignItems="center">
          {!hideSideBarToggle && (
            <Tooltip title={t('navigation.sidebar')}>
              <IconButton color="inherit" onClick={toggleSidebar} data-testid="toggle-sidebar">
                <Icons.Menu />
              </IconButton>
            </Tooltip>
          )}
          <Projects />
          <UnitControl />
          {canSeeBlueprints(user?.role) && <Blueprints />}
          {canSeeAlerts(user?.role) && <Alerts />}
        </Box>
        {isLogoVisible && (
          <Box mt={1}>
            <NavLink to="/">
              <GreenerLogo logoColor={theme.palette.brand['lime-green']} style={{ marginTop: '-8px' }} />
            </NavLink>
          </Box>
        )}
        <Box gap={1} display="flex" alignItems="center">
          <TopBarSearch openSearchDrawer={openSearchDrawer} />
          <Account />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
